import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { MediaPlan } from '@mint-sdk/api-media-plan';

import { LOADING_TOTALS } from '../../consts/flights.js';

type Params = {
  flightId: string;
  activityId: string;
  data: MediaPlan.UpdateFlightRequest;
};

export const useUpdateFlightsApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flightId, data }: Params) =>
      MediaPlan.updateFlights({
        urlParams: { flightId },
        data,
      }),
    onMutate: async ({ flightId, data, activityId }) => {
      const queryKey = MediaPlan.getFlightsListQueryOptions({
        params: { activityId },
      }).queryKey;

      await queryClient.cancelQueries({ queryKey });

      const previousFlights =
        queryClient.getQueryData<MediaPlan.FlightsList>(queryKey);

      queryClient.setQueryData<MediaPlan.FlightsList>(
        queryKey,
        (oldFlights?: MediaPlan.FlightsList) => {
          if (!oldFlights?.data) {
            return;
          }
          return {
            ...oldFlights,
            data: {
              ...oldFlights.data,
              items: oldFlights.data.items.map((flight) => {
                if (flight.id === flightId) {
                  return {
                    ...flight,
                    ...data,
                  };
                }
                return flight;
              }),
              additional:
                'name' in data
                  ? oldFlights.data.additional
                  : {
                      ...oldFlights.data.additional,
                      totals: LOADING_TOTALS,
                      customFlightCanBeCreated: true,
                      isLastFlightDeletable: false,
                    },
            },
          };
        },
      );

      return { previousFlights };
    },
    onError: (_, { activityId }, context) => {
      queryClient.setQueryData<MediaPlan.FlightsList>(
        MediaPlan.getFlightsListQueryOptions({ params: { activityId } })
          .queryKey,
        context?.previousFlights,
      );
    },
    onSuccess: (_, { data }) => {
      if (data.dateFrom || data.dateTo) {
        const restrictionsKey = MediaPlan.getPlanningRestrictionsQueryOptions({
          params: { mediaDataId: 'unknown' },
        }).queryKey.slice(0, -1); // we don't know mediaDataId, so remove last segment

        queryClient.invalidateQueries({
          queryKey: restrictionsKey,
        });
      }
    },
    onSettled: (_, __, { activityId }) => {
      queryClient.invalidateQueries(
        MediaPlan.getFlightsListQueryOptions({
          params: { activityId },
        }),
      );
    },
  });
};
